import { useReducer, type ReactNode } from 'react';
import { LoginFlowContext, type LoginFlowAction, type LoginFlowState } from './flowContext';

const initial: LoginFlowState = { step: 'idle' };

function reducer(state: LoginFlowState, action: LoginFlowAction): LoginFlowState {
  switch (action.type) {
    case 'SUBMIT_CREDENTIALS':
      return { step: 'submitting_credentials' };

    case 'MFA_CHALLENGE':
      return { step: 'mfa_challenge', challengeToken: action.challengeToken };

    case 'MFA_ENROLLMENT':
      return {
        step: 'mfa_enrollment',
        challengeToken: action.challengeToken,
        qrCodeUri: action.qrCodeUri,
        manualEntryKey: action.manualEntryKey,
      };

    case 'RECOVERY_CODES':
      return {
        step: 'recovery_codes_shown',
        recoveryCodes: action.recoveryCodes,
        pendingTokens: action.pendingTokens,
        pendingUser: action.pendingUser,
      };

    case 'AUTHENTICATED':
      return { step: 'authenticated' };

    case 'ERROR':
      return { step: 'error', message: action.message };

    case 'RATE_LIMITED': {
      if (state.step === 'mfa_challenge') {
        return { ...state, rateLimitUntil: Date.now() + action.retryAfterSeconds * 1000 };
      }
      if (state.step === 'mfa_enrollment') {
        return { ...state, rateLimitUntil: Date.now() + action.retryAfterSeconds * 1000 };
      }
      return state;
    }

    case 'CLEAR_RATE_LIMIT': {
      if (state.step === 'mfa_challenge') {
        return { step: 'mfa_challenge', challengeToken: state.challengeToken };
      }
      if (state.step === 'mfa_enrollment') {
        return {
          step: 'mfa_enrollment',
          challengeToken: state.challengeToken,
          qrCodeUri: state.qrCodeUri,
          manualEntryKey: state.manualEntryKey,
        };
      }
      return state;
    }

    case 'RESET':
      return initial;

    default:
      return state;
  }
}

export function LoginFlowProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initial);
  return (
    <LoginFlowContext.Provider value={{ state, dispatch }}>
      {children}
    </LoginFlowContext.Provider>
  );
}
